import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Shield, AlertTriangle, Activity, TrendingUp, TrendingDown, BarChart3,
  Loader2, Check, X, ChevronRight, Eye, Zap, RotateCcw, Users
} from 'lucide-react';

const SAFETY_THRESHOLDS = { rollback: 10, override: 15 };

export default function AutonomyMonitoring() {
  const [dashboard, setDashboard] = useState(null);
  const [patternMetrics, setPatternMetrics] = useState([]);
  const [suggestionMetrics, setSuggestionMetrics] = useState([]);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAction, setSelectedAction] = useState(null);
  const [tuningConfigs, setTuningConfigs] = useState({});
  const [readiness, setReadiness] = useState({ readiness: [], summary: {} });
  const [suppressed, setSuppressed] = useState({ suppressed: [], reason_summary: {} });
  const [firstWatch, setFirstWatch] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [viewMode, setViewMode] = useState('all'); // 'all' | 'real' | 'demo'

  const loadData = (mode) => {
    const demoParam = mode === 'demo' ? '?demo=true' : mode === 'real' ? '?demo=false' : '';
    setLoading(true);
    Promise.all([
      axios.get(`${API}/support-v2/staff/autonomy-dashboard`).catch(() => ({ data: {} })),
      axios.get(`${API}/support-v2/staff/pattern-metrics`).catch(() => ({ data: { patterns: [] } })),
      axios.get(`${API}/support-v2/staff/suggestion-metrics`).catch(() => ({ data: { suggestions: [] } })),
      axios.get(`${API}/support-v2/staff/weekly-intelligence`).catch(() => ({ data: {} })),
      axios.get(`${API}/support-v2/admin/tuning-config`).catch(() => ({ data: { configs: {} } })),
      axios.get(`${API}/support-v2/v3/autonomy/readiness${demoParam}`).catch(() => ({ data: { readiness: [], summary: {} } })),
      axios.get(`${API}/support-v2/v3/autonomy/suppressed-actions${demoParam}`).catch(() => ({ data: { suppressed: [] } })),
      axios.get(`${API}/support-v2/v3/autonomy/first-actions-watch`).catch(() => ({ data: { first_actions: [] } })),
      axios.get(`${API}/support-v2/v3/autonomy/activation-recommendations`).catch(() => ({ data: { recommendations: [] } })),
    ]).then(([dashRes, patRes, sugRes, weekRes, tuneRes, readRes, supRes, watchRes, recRes]) => {
      setDashboard(dashRes.data);
      setPatternMetrics(patRes.data?.patterns || []);
      setSuggestionMetrics(sugRes.data?.suggestions || []);
      setWeeklyReport(weekRes.data);
      setTuningConfigs(tuneRes.data?.configs || {});
      setReadiness(readRes.data || { readiness: [], summary: {} });
      setSuppressed(supRes.data || { suppressed: [] });
      setFirstWatch(watchRes.data?.first_actions || []);
      setRecommendations(recRes.data?.recommendations || []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { loadData(viewMode); }, [viewMode]);

  const handleTune = async (patternType, field, value) => {
    try {
      await axios.put(`${API}/support-v2/admin/tuning-config/${patternType}`, { [field]: value });
      toast.success(`${patternType}: ${field} updated`);
      const res = await axios.get(`${API}/support-v2/admin/tuning-config`);
      setTuningConfigs(res.data?.configs || {});
    } catch { toast.error('Update failed'); }
  };

  const rollbackRate = dashboard ? dashboard.rollback_rate || 0 : 0;
  const rollbackSafe = rollbackRate < SAFETY_THRESHOLDS.rollback;

  if (loading) return <AdminLayout><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 text-adm-accent animate-spin" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6" data-testid="autonomy-monitoring">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-['Outfit'] font-semibold text-adm-primary">Autonomy Monitoring</h1>
            <p className="text-xs text-adm-tertiary mt-1">Control room for autonomous care operations</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center bg-white/[0.03] rounded-lg p-0.5 border border-white/[0.06]" data-testid="autonomy-view-toggle">
              {[
                { key: 'all', label: 'All' },
                { key: 'real', label: 'Real' },
                { key: 'demo', label: 'Demo' },
              ].map(m => (
                <button
                  key={m.key}
                  onClick={() => setViewMode(m.key)}
                  className={`px-2.5 py-1 rounded-md text-[10px] font-['Work_Sans'] transition-colors ${viewMode === m.key ? 'bg-adm-accent/15 text-adm-accent' : 'text-adm-tertiary hover:text-adm-secondary'}`}
                  data-testid={`autonomy-view-${m.key}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <Badge className={`text-xs ${rollbackSafe ? 'bg-emerald-500/15 text-emerald-400' : 'bg-rose-500/15 text-rose-400'}`}>
              <Shield className="w-3 h-3 mr-1" /> Autonomy {rollbackSafe ? 'HEALTHY' : 'NEEDS ATTENTION'}
            </Badge>
          </div>
        </div>

        {/* ═══ 1. SYSTEM STATUS ═══ */}
        <div className="grid grid-cols-5 gap-3" data-testid="status-bar">
          {[
            { label: 'Auto-Actions (7d)', value: dashboard?.auto_actions_7d || 0, color: '#5C6B8A' },
            { label: 'Rollbacks (7d)', value: dashboard?.rollbacks_7d || 0, color: rollbackSafe ? '#4E7A6B' : '#C85C5C' },
            { label: 'Rollback Rate', value: `${rollbackRate}%`, color: rollbackSafe ? '#4E7A6B' : '#C85C5C' },
            { label: 'Pending Evolutions', value: dashboard?.pending_evolutions || 0, color: '#D4A843' },
            { label: 'Active Patterns', value: patternMetrics.filter(p => p.active_patterns > 0).length, color: '#8C6B55' },
          ].map(m => (
            <Card key={m.label} className="bg-adm-panel border-white/[0.06]"><CardContent className="p-3 text-center">
              <p className="text-2xl font-['Outfit'] font-light" style={{ color: m.color }}>{m.value}</p>
              <p className="text-[8px] uppercase tracking-wider text-adm-tertiary">{m.label}</p>
            </CardContent></Card>
          ))}
        </div>

        {/* ═══ 2. SAFETY INDICATORS ═══ */}
        <Card className="bg-adm-panel border-white/[0.06]" data-testid="safety-panel"><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wider text-adm-tertiary mb-3">Safety Indicators</p>
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: 'Rollback Rate', value: `${rollbackRate}%`, target: '<10%', ok: rollbackRate < 10 },
              { label: 'Conflict Incidents', value: '0', target: '0', ok: true },
              { label: 'SLA Breach (autonomy)', value: '0', target: '0', ok: true },
              { label: 'Patterns Active', value: patternMetrics.filter(p => p.active_patterns > 0).length, target: 'monitoring', ok: true },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-2 p-2 rounded-lg" style={{ backgroundColor: s.ok ? '#4E7A6B10' : '#C85C5C10' }}>
                {s.ok ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <AlertTriangle className="w-3.5 h-3.5 text-rose-400" />}
                <div>
                  <p className="text-xs text-adm-primary">{s.value}</p>
                  <p className="text-[8px] text-adm-tertiary">{s.label} (target: {s.target})</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent></Card>

        {/* ═══ ACTIVATION READINESS ═══ */}
        <Card className="bg-adm-panel border-white/[0.06]" data-testid="readiness-panel"><CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] uppercase tracking-wider text-adm-tertiary">Activation Readiness</p>
            <div className="flex gap-2 text-[8px]">
              <Badge className="bg-emerald-500/10 text-emerald-400 border-0">{readiness.summary?.ready || 0} ready</Badge>
              <Badge className="bg-amber-500/10 text-amber-400 border-0">{readiness.summary?.nearly_ready || 0} nearly</Badge>
              <Badge className="bg-white/5 text-adm-tertiary border-0">{readiness.summary?.approaching || 0} approaching</Badge>
            </div>
          </div>
          <div className="space-y-2">
            {readiness.readiness?.slice(0, 6).map(r => {
              const stateColors = { ready: '#4E7A6B', nearly_ready: '#D4A843', approaching: '#5C6B8A', not_ready: '#8A8681' };
              return (
                <div key={r.pattern_id} className="flex items-center gap-3 py-2 border-b border-white/[0.03] last:border-0">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-mono" style={{ backgroundColor: (stateColors[r.readiness_state] || '#8A8681') + '15', color: stateColors[r.readiness_state] }}>
                    {r.readiness_score}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-adm-primary">{r.subscriber_name}</span>
                      <Badge className="text-[7px] border-0" style={{ backgroundColor: (stateColors[r.readiness_state] || '#8A8681') + '15', color: stateColors[r.readiness_state] }}>
                        {r.readiness_state?.replace('_', ' ')}
                      </Badge>
                    </div>
                    <p className="text-[9px] text-adm-tertiary capitalize">{r.pattern_type?.replace('_', ' ')} — {r.current_confidence}</p>
                  </div>
                  <div className="text-right max-w-[180px]">
                    {r.blockers?.[0] && <p className="text-[8px] text-rose-400 truncate">{r.blockers[0]}</p>}
                    {r.enablers?.[0] && <p className="text-[8px] text-emerald-400 truncate">{r.enablers[0]}</p>}
                  </div>
                  {r.recommendation && (
                    <Badge variant="outline" className="text-[7px] border-white/10 text-adm-tertiary">{r.recommendation.type?.replace('_', ' ')}</Badge>
                  )}
                </div>
              );
            })}
            {(!readiness.readiness || readiness.readiness.length === 0) && <p className="text-xs text-adm-tertiary text-center py-3">No active patterns</p>}
          </div>
        </CardContent></Card>

        {/* ═══ SUPPRESSED ACTIONS ═══ */}
        <Card className="bg-adm-panel border-white/[0.06]" data-testid="suppressed-panel"><CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] uppercase tracking-wider text-adm-tertiary">Suppressed Actions</p>
            <span className="text-[9px] text-adm-tertiary">{suppressed.total_suppressed || 0} total</span>
          </div>
          {suppressed.reason_summary && Object.keys(suppressed.reason_summary).length > 0 && (
            <div className="flex gap-2 mb-3 flex-wrap">
              {Object.entries(suppressed.reason_summary).map(([reason, count]) => (
                <Badge key={reason} variant="outline" className="border-white/10 text-adm-tertiary text-[7px]">{reason.replace('_', ' ')}: {count}</Badge>
              ))}
            </div>
          )}
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {suppressed.suppressed?.slice(0, 8).map(s => (
              <div key={s.id} className="flex items-center gap-3 py-1.5 border-b border-white/[0.03] last:border-0">
                <X className="w-3 h-3 text-rose-400/50 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[9px] text-adm-secondary truncate capitalize">{s.subscriber_name} — {s.proposed_action_type?.replace('_', ' ')}</p>
                  <p className="text-[8px] text-adm-tertiary truncate">{s.suppression_reason_text}</p>
                </div>
                <span className="text-[8px] text-adm-tertiary">{s.count}x</span>
                {s.would_have_executed_if?.[0] && <span className="text-[7px] text-amber-400 truncate max-w-[100px]">if: {s.would_have_executed_if[0]}</span>}
              </div>
            ))}
            {(!suppressed.suppressed || suppressed.suppressed.length === 0) && <p className="text-xs text-adm-tertiary text-center py-3">No suppressed actions</p>}
          </div>
        </CardContent></Card>

        {/* ═══ FIRST AUTO-ACTION WATCH + RECOMMENDATIONS ═══ */}
        <div className="grid grid-cols-2 gap-5">
          <Card className="bg-adm-panel border-white/[0.06]" data-testid="first-watch"><CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-wider text-adm-tertiary mb-3">First Auto-Action Watch</p>
            {firstWatch.length > 0 ? firstWatch.map((w, i) => (
              <div key={i} className="flex items-center gap-2 py-1.5 text-[9px]">
                <Zap className="w-3 h-3 text-amber-400" />
                <span className="text-adm-secondary">{w.subscriber_name}</span>
                <span className="text-adm-tertiary capitalize">{w.action_type?.replace('_', ' ')}</span>
                {w.rolled_back && <Badge className="bg-rose-500/10 text-rose-400 border-0 text-[7px]">rolled back</Badge>}
              </div>
            )) : <p className="text-xs text-adm-tertiary text-center py-6">No first-time auto-actions yet — this is expected before patterns reach high confidence</p>}
          </CardContent></Card>

          <Card className="bg-adm-panel border-white/[0.06]" data-testid="recommendations"><CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-wider text-adm-tertiary mb-3">Activation Recommendations</p>
            {recommendations.length > 0 ? recommendations.map((r, i) => (
              <div key={i} className="p-2 rounded-lg bg-white/[0.02] mb-2">
                <div className="flex items-center gap-2 mb-1">
                  <Badge className="text-[7px] border-0 capitalize" style={{ backgroundColor: r.risk === 'low' ? '#4E7A6B15' : r.risk === 'medium' ? '#D4A84315' : '#8A868115', color: r.risk === 'low' ? '#4E7A6B' : r.risk === 'medium' ? '#D4A843' : '#8A8681' }}>
                    {r.type?.replace('_', ' ')}
                  </Badge>
                  <span className="text-[8px] text-adm-tertiary capitalize">{r.pattern_type?.replace('_', ' ')}</span>
                </div>
                <p className="text-[9px] text-adm-secondary">{r.text}</p>
                <p className="text-[8px] text-adm-tertiary">{r.rationale}</p>
              </div>
            )) : <p className="text-xs text-adm-tertiary text-center py-6">No recommendations — patterns still building evidence</p>}
          </CardContent></Card>
        </div>

        <div className="grid grid-cols-2 gap-5">
          {/* ═══ 3. PATTERN → ACTION EFFECTIVENESS ═══ */}
          <Card className="bg-adm-panel border-white/[0.06]" data-testid="pattern-effectiveness"><CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-wider text-adm-tertiary mb-3">Pattern → Action Effectiveness</p>
            <div className="space-y-2">
              {patternMetrics.filter(p => p.total_detected > 0).map(p => (
                <div key={p.pattern_type} className="flex items-center gap-3 py-1.5 border-b border-white/[0.03] last:border-0">
                  <div className="flex-1">
                    <p className="text-xs text-adm-primary">{p.label}</p>
                    <p className="text-[9px] text-adm-tertiary">detected: {p.total_detected} | tickets: {p.linked_tickets}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-mono" style={{ color: p.suggestion_acceptance_rate >= 50 ? '#4E7A6B' : '#D4A843' }}>
                      {p.suggestion_acceptance_rate}% accepted
                    </p>
                    {p.false_positive_count > 0 && <p className="text-[8px] text-rose-400">{p.false_positive_count} FP</p>}
                  </div>
                </div>
              ))}
              {patternMetrics.filter(p => p.total_detected > 0).length === 0 && (
                <p className="text-xs text-adm-tertiary text-center py-4">No patterns detected yet</p>
              )}
            </div>
          </CardContent></Card>

          {/* ═══ 4. SUGGESTION PERFORMANCE ═══ */}
          <Card className="bg-adm-panel border-white/[0.06]" data-testid="suggestion-performance"><CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-wider text-adm-tertiary mb-3">Suggestion Performance</p>
            <div className="space-y-2">
              {suggestionMetrics.filter(s => s.total > 0).map(s => (
                <div key={s.action_type} className="flex items-center gap-3 py-1.5 border-b border-white/[0.03] last:border-0">
                  <div className="flex-1">
                    <p className="text-xs text-adm-primary capitalize">{s.action_type.replace('_', ' ')}</p>
                    <p className="text-[9px] text-adm-tertiary">total: {s.total}</p>
                  </div>
                  <div className="flex gap-2">
                    <Badge className="bg-emerald-500/10 text-emerald-400 border-0 text-[8px]">{s.accepted} accepted</Badge>
                    {s.rejected > 0 && <Badge className="bg-rose-500/10 text-rose-400 border-0 text-[8px]">{s.rejected} rejected</Badge>}
                    {s.modified > 0 && <Badge className="bg-amber-500/10 text-amber-400 border-0 text-[8px]">{s.modified} modified</Badge>}
                  </div>
                </div>
              ))}
              {suggestionMetrics.filter(s => s.total > 0).length === 0 && (
                <p className="text-xs text-adm-tertiary text-center py-4">No suggestions generated yet</p>
              )}
            </div>
          </CardContent></Card>
        </div>

        {/* ═══ 5. WEEKLY INTELLIGENCE ═══ */}
        {weeklyReport && (
          <Card className="bg-adm-panel border-white/[0.06]" data-testid="weekly-report"><CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-wider text-adm-tertiary mb-3">Weekly Intelligence</p>
            <div className="grid grid-cols-5 gap-3 mb-3">
              {[
                { label: 'Tickets', value: weeklyReport.total_tickets || 0 },
                { label: 'Resolved', value: weeklyReport.resolved || 0 },
                { label: 'Escalated', value: weeklyReport.escalated || 0 },
                { label: 'Patterns', value: weeklyReport.patterns_detected || 0 },
                { label: 'Suggestions', value: weeklyReport.suggestions_generated || 0 },
              ].map(m => (
                <div key={m.label} className="text-center">
                  <p className="text-lg font-['Outfit'] font-light text-adm-primary">{m.value}</p>
                  <p className="text-[8px] text-adm-tertiary uppercase">{m.label}</p>
                </div>
              ))}
            </div>
            {weeklyReport.category_distribution && Object.keys(weeklyReport.category_distribution).length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {Object.entries(weeklyReport.category_distribution).map(([cat, count]) => (
                  <Badge key={cat} variant="outline" className="border-white/10 text-adm-tertiary text-[8px] capitalize">{cat.replace('_', ' ')}: {count}</Badge>
                ))}
              </div>
            )}
          </CardContent></Card>
        )}

        {/* ═══ 6. QUICK TUNING ═══ */}
        <Card className="bg-adm-panel border-white/[0.06]" data-testid="tuning-panel"><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wider text-adm-tertiary mb-3">Pattern Tuning Controls</p>
          <div className="space-y-3">
            {Object.entries(tuningConfigs).map(([pt, cfg]) => {
              const def = patternMetrics.find(p => p.pattern_type === pt);
              return (
                <div key={pt} className="flex items-center gap-4 py-2 border-b border-white/[0.03] last:border-0">
                  <div className="w-40">
                    <p className="text-xs text-adm-primary capitalize">{pt.replace('_', ' ')}</p>
                    <p className="text-[8px] text-adm-tertiary">{def ? `${def.total_detected} detected` : 'no data'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] text-adm-tertiary w-14">Freq ≥</span>
                    <Input type="number" value={cfg.frequency_threshold} className="bg-white/5 border-white/10 text-adm-primary h-7 w-14 text-xs"
                      onChange={e => handleTune(pt, 'frequency_threshold', parseInt(e.target.value))} />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] text-adm-tertiary w-14">Weight</span>
                    <Input type="number" step="0.1" value={cfg.severity_weight} className="bg-white/5 border-white/10 text-adm-primary h-7 w-14 text-xs"
                      onChange={e => handleTune(pt, 'severity_weight', parseFloat(e.target.value))} />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] text-adm-tertiary w-16">Cooldown</span>
                    <Input type="number" value={cfg.cooldown_hours} className="bg-white/5 border-white/10 text-adm-primary h-7 w-14 text-xs"
                      onChange={e => handleTune(pt, 'cooldown_hours', parseInt(e.target.value))} />
                    <span className="text-[8px] text-adm-tertiary">h</span>
                  </div>
                  <Button size="sm" variant="outline"
                    onClick={() => handleTune(pt, 'is_enabled', !cfg.is_enabled)}
                    className={`h-6 text-[8px] ${cfg.is_enabled ? 'border-emerald-500/20 text-emerald-400' : 'border-rose-500/20 text-rose-400'}`}>
                    {cfg.is_enabled ? 'Enabled' : 'Disabled'}
                  </Button>
                </div>
              );
            })}
          </div>
        </CardContent></Card>
      </div>
    </AdminLayout>
  );
}
